import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminSupportTickets = () => {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['OPEN', 'ASSIGNED', 'RESOLVE', 'INPROGRESS']);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTickets, setFilteredTickets] = useState([]);

  // Assign modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignModalTicket, setAssignModalTicket] = useState(null);
  const [supportAgents, setSupportAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [assigning, setAssigning] = useState(false);

  const statusOptions = [
    { value: 'OPEN',       label: 'Open (Unassigned)' },
    { value: 'ASSIGNED',   label: 'Assigned' },
    { value: 'INPROGRESS', label: 'In Progress' },
    { value: 'RESOLVE',    label: 'Resolved' },
  ];

  useEffect(() => {
    loadTickets();
  }, [selectedStatuses]);

  useEffect(() => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter(t =>
        t.ticketId?.toLowerCase().includes(s) ||
        t.agentId?.toLowerCase().includes(s) ||
        t.agentName?.toLowerCase().includes(s) ||
        t.agentEmail?.toLowerCase().includes(s) ||
        t.agentQuery?.toLowerCase().includes(s) ||
        t.ticketStatus?.toLowerCase().includes(s) ||
        t.assignedTo?.toLowerCase().includes(s) ||
        t.assignedToName?.toLowerCase().includes(s)
      ));
    }
  }, [searchTerm, tickets]);

  // ─── Load tickets ─────────────────────────────────────────────────────────
  // Uses GET /api/admin/support-tickets?status=... (ISupportAgentTickets.getAllTicketsByStatuses)
  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getSupportTickets(selectedStatuses);
      if (response.success) {
        setTickets(response.data?.tickets || []);
        setFilteredTickets(response.data?.tickets || []);
      } else {
        toast.error(response.message || 'Failed to load tickets');
      }
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  // ─── Load support agents ──────────────────────────────────────────────────
  // Uses GET /api/admin/agent-tickets/support-agents → [{supportName, supportEmail, supportId}]
  const loadSupportAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await adminApiService.getSupportAgents();
      if (response.success) {
        setSupportAgents(response.data || []);
      } else {
        toast.error('Failed to load support agents');
      }
    } catch (err) {
      toast.error('Failed to load support agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  const openAssignModal = (e, ticket) => {
    e.stopPropagation();
    setAssignModalTicket(ticket);
    setSelectedAssignee('');
    setShowAssignModal(true);
    loadSupportAgents();
  };

  // ─── Assign ticket ────────────────────────────────────────────────────────
  // Uses POST /api/admin/agent-tickets/assign-ticket  { ticketId, assignedTo }
  // ticketId is the ticket's ticketId field; assignedTo is supportId (or 'ADMIN_SELF')
  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedAssignee) {
      toast.error('Please select an assignee');
      return;
    }
    try {
      setAssigning(true);
      const response = await adminApiService.assignTicket(
        assignModalTicket.ticketId,   // pass the ticketId field (not MongoDB id)
        selectedAssignee              // supportId from dropdown or 'ADMIN_SELF'
      );
      if (response.success) {
        toast.success('Ticket assigned successfully!');
        setShowAssignModal(false);
        setAssignModalTicket(null);
        await loadTickets();
      } else {
        toast.error(response.message || 'Failed to assign ticket');
      }
    } catch (err) {
      toast.error('Failed to assign ticket');
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const getStatusBadge = (status) => {
    const map = {
      OPEN:       'bg-orange-100 text-orange-800',
      ASSIGNED:   'bg-blue-100 text-blue-800',
      RESOLVE:    'bg-green-100 text-green-800',
      INPROGRESS: 'bg-yellow-100 text-yellow-800',
      CLOSED:     'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'UNKNOWN'}
      </span>
    );
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600">Assign and monitor all agent support tickets</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">

          {/* Filters */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Tickets</h3>
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
                {statusOptions.map(s => (
                  <label key={s.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(s.value)}
                      onChange={() => handleStatusToggle(s.value)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">{s.label}</span>
                  </label>
                ))}
                <button
                  onClick={loadTickets}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
            <div className="mt-3 text-sm text-gray-500">Total: {filteredTickets.length} tickets</div>
          </div>

          {/* Search */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="relative max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by ticket ID, agent ID, name, email or query..."
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Ticket ID', 'Agent ID', 'Agent Name', 'Agent Email', 'Query', 'Date', 'Status', 'Assigned To', 'Action'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTickets.length > 0 ? filteredTickets.map((ticket, i) => {
                  const isUnassigned = !ticket.assignedTo || ticket.assignedTo === 'N/A' || ticket.ticketStatus === 'OPEN';
                  const assigneeDisplayName = ticket.assignedToName && ticket.assignedToName !== '-' && ticket.assignedToName !== 'N/A'
                    ? ticket.assignedToName
                    : ticket.assignedTo;

                  return (
                    <tr key={ticket.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-blue-600 hover:text-blue-800">
                        <Link to={`/admin/dashboard/support-tickets/${ticket.id}`} className="hover:underline">
                          {ticket.ticketId || '—'}
                        </Link>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.agentId || '—'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.agentName || '—'}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{ticket.agentEmail || '—'}</td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-xs">
                        <div className="truncate" title={ticket.agentQuery}>{ticket.agentQuery || '—'}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate(ticket.createdDate)}</td>
                      <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(ticket.ticketStatus)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {isUnassigned ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                            <i className="fas fa-question-circle mr-1 text-gray-400"></i> Unassigned
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            <i className="fas fa-user-check mr-1 text-blue-500"></i>
                            {assigneeDisplayName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => openAssignModal(e, ticket)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-medium rounded-md transition-colors shadow-sm ${
                            isUnassigned
                              ? 'bg-green-600 hover:bg-green-700'
                              : 'bg-indigo-600 hover:bg-indigo-700'
                          }`}
                        >
                          <i className="fas fa-user-tag"></i> {isUnassigned ? 'Assign' : 'Reassign'}
                        </button>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-10 text-center text-gray-500">
                      <i className="fas fa-ticket-alt text-3xl text-gray-300 mb-3 block"></i>
                      {searchTerm ? 'No tickets match your search.' : 'No support tickets found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredTickets.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
              Showing {filteredTickets.length} of {tickets.length} tickets
              {searchTerm && <span className="ml-2">(filtered by "{searchTerm}")</span>}
            </div>
          )}
        </div>
      </div>

      {/* ─── Assign Ticket Modal ──────────────────────────────────────────── */}
      {showAssignModal && assignModalTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">

            <div className="flex justify-between items-center mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <i className="fas fa-user-tag text-green-600"></i>
                Assign Ticket
              </h3>
              <button
                onClick={() => { setShowAssignModal(false); setAssignModalTicket(null); }}
                className="text-gray-400 hover:text-gray-600 text-lg"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Ticket summary */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-1 text-sm">
              <p><span className="font-medium text-gray-600">Ticket ID:</span> {assignModalTicket.ticketId}</p>
              <p><span className="font-medium text-gray-600">Agent:</span> {assignModalTicket.agentName} ({assignModalTicket.agentEmail})</p>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">Status:</span>
                {getStatusBadge(assignModalTicket.ticketStatus)}
              </div>
            </div>

            <form onSubmit={handleAssign} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
                {loadingAgents ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                    Loading agents...
                  </div>
                ) : (
                  <select
                    value={selectedAssignee}
                    onChange={e => setSelectedAssignee(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:outline-none"
                  >
                    <option value="">— Select Assignee —</option>
                    <option value="ADMIN_SELF">🧑‍💼 Assign to Myself (Admin)</option>
                    {supportAgents.map(agent => (
                      <option key={agent.supportId} value={agent.supportId}>
                        {agent.supportName} — {agent.supportEmail}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAssignModal(false); setAssignModalTicket(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assigning || !selectedAssignee}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {assigning ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Assigning...</>
                  ) : (
                    <><i className="fas fa-check"></i>Assign Ticket</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupportTickets;